import { useEffect, useMemo, useState } from 'react'

import { InjectionTokens } from '@/controller/tokens'

import { RouteNavigationState, RouteNavigationStateProps } from '@/domain/states/RouteNavigationState'
import { RouteState, RouteStateProps } from '@/domain/states/RouteState'

import { MapTocAndTod } from '../components/MapTocAndTod'
import { RubberbandStatus } from '../interfaces/MapRouteEvents'

import { useGlobalState } from '@/presentation/hooks/useGlobalState'
import { isSet } from '@/utils/validation'

function MapTocAndTodPresenter() {
  const [routeState] = useGlobalState<RouteState, RouteStateProps>(InjectionTokens.RouteState)
  const [routeNavigationState] = useGlobalState<RouteNavigationState, RouteNavigationStateProps>(
    InjectionTokens.RouteNavigationState
  )
  const [hideTocAndTod, setHideTocAndTod] = useState(false)

  useEffect(() => {
    const rubberbandSubscription = RubberbandStatus.subscribe((status) => setHideTocAndTod(status))
    return () => rubberbandSubscription.unsubscribe()
  }, [])

  const waypoints = useMemo(() => routeState.activeRoute?.waypoints ?? [], [routeState.activeRoute?.waypoints])

  const distanceToTocOnStage = useMemo(() => {
    const tocIndex = routeNavigationState?.verticalInfo?.tocIndex
    const distanceToToc = routeNavigationState?.verticalInfo?.distanceToToc
    if (!isSet(tocIndex) || !isSet(distanceToToc)) return null

    const nextWaypointIndex = tocIndex
    const nextWaypoint = waypoints[nextWaypointIndex]

    if (!isSet(nextWaypoint)) return null

    const nextWaypointDistanceOnStage = routeNavigationState?.userToWaypointInfo?.get(nextWaypoint?.id)?.distanceStage
    const nextWaypointDistanceOnRoute = routeNavigationState?.userToWaypointInfo?.get(nextWaypoint?.id)?.distanceOnRoute

    if (!isSet(nextWaypointDistanceOnStage) || !isSet(nextWaypointDistanceOnRoute)) return null

    const startOnRouteDistance = nextWaypointDistanceOnRoute - nextWaypointDistanceOnStage

    const distanceToTocOnStage = distanceToToc - startOnRouteDistance
    if (distanceToTocOnStage < 0) return null

    return distanceToTocOnStage
  }, [routeNavigationState.verticalInfo, waypoints])

  const distanceToTodOnStage = useMemo(() => {
    const todIndex = routeNavigationState?.verticalInfo?.todIndex
    const distanceToTod = routeNavigationState?.verticalInfo?.distanceToTod
    if (!isSet(todIndex) || !isSet(distanceToTod)) return null

    const nextWaypointIndex = todIndex - 1
    const nextWaypoint = waypoints[nextWaypointIndex]

    if (!isSet(nextWaypoint)) return null

    const nextWaypointDistanceOnStage = routeNavigationState?.userToWaypointInfo?.get(nextWaypoint?.id)?.distanceStage
    const nextWaypointDistanceOnRoute = routeNavigationState?.userToWaypointInfo?.get(nextWaypoint?.id)?.distanceOnRoute

    if (!isSet(nextWaypointDistanceOnStage) || !isSet(nextWaypointDistanceOnRoute)) return null

    const startOnRouteDistance = nextWaypointDistanceOnRoute - nextWaypointDistanceOnStage

    const distanceToTodOnStage = distanceToTod - startOnRouteDistance
    if (distanceToTodOnStage < 0) return null

    return distanceToTodOnStage
  }, [routeNavigationState.verticalInfo, waypoints])

  const tocRouteLegCoords = useMemo(() => {
    const tocIndex = routeNavigationState?.verticalInfo?.tocIndex
    if (!isSet(tocIndex)) return null

    const previousWaypointIndex = tocIndex - 1
    const nextWaypointIndex = tocIndex
    if (!waypoints[previousWaypointIndex] || !waypoints[nextWaypointIndex]) {
      return null
    }

    return [waypoints[previousWaypointIndex].coordinates, waypoints[nextWaypointIndex].coordinates]
  }, [routeNavigationState.verticalInfo, waypoints])

  const todRouteLegCoords = useMemo(() => {
    const todIndex = routeNavigationState?.verticalInfo?.todIndex
    if (!isSet(todIndex)) return null

    const previousWaypointIndex = todIndex - 2
    const nextWaypointIndex = todIndex - 1
    if (!waypoints[previousWaypointIndex] || !waypoints[nextWaypointIndex]) {
      return null
    }

    return [waypoints[previousWaypointIndex].coordinates, waypoints[nextWaypointIndex].coordinates]
  }, [routeNavigationState.verticalInfo, waypoints])

  if (!isSet(routeNavigationState.userToWaypointInfo) || !isSet(routeNavigationState.verticalInfo)) {
    return
  }

  if (!isSet(waypoints[0].coordinates?.altitude) || !isSet(waypoints[waypoints.length - 1].coordinates?.altitude)) {
    return
  }

  if (hideTocAndTod) return null

  return (
    <MapTocAndTod
      distanceToTocOnStage={distanceToTocOnStage}
      tocRouteLegCoords={tocRouteLegCoords}
      distanceToTodOnStage={distanceToTodOnStage}
      todRouteLegCoords={todRouteLegCoords}
    />
  )
}

export { MapTocAndTodPresenter }
